import React, {useEffect, useRef, useState} from 'react';
import {Button} from "primereact/button";
import {Dialog} from "primereact/dialog";
import {RadioButton} from "primereact/radiobutton";
import {InputNumber} from "primereact/inputnumber";
import {MultiSelect} from "primereact/multiselect";
import {DataTable} from "primereact/datatable";
import {Column} from "primereact/column";
import {Message} from "primereact/message";
import {SelectButton} from "primereact/selectbutton";
import {Dropdown} from "primereact/dropdown";
import {Checkbox} from "primereact/checkbox";
import {FileUpload} from "primereact/fileupload";

import {AppContext} from "../../HomePage";
import {CampaignContext} from "../../App";
import {ProductService} from "../../service/ProductService";
import {ProductMatchingService} from "../../service/ProductMatchingService";
import {SpecialFieldService} from "../../service/SpecialFieldService";
import {TemplateService} from "../../service/TemplateService";
import {ProductExcelImportService} from "../../service/ProductExcelImportService";
import {CampaignService} from "../../service/CampaignService";
import {OnboardingService} from "../../service/OnboardingService";
import {ProductSuggestionService} from "../../service/ProductSuggestionService";
import {QueueService} from "../../service/QueueService";
import {Loading} from "../../utils/InlineComponents";
import {exportFile, getHostName, isAdmin} from "../../utils/Utils";

const productService = new ProductService();
const productMatchingService = new ProductMatchingService();
const specialFieldService = new SpecialFieldService();
const templateService = new TemplateService();
const productExcelImportService = new ProductExcelImportService();
const campaignService = new CampaignService();
const onboardingService = new OnboardingService();
const productSuggestionService = new ProductSuggestionService();
const queueService = new QueueService();

// Base product code fields (special fields are appended from the backend).
const BASE_FIELDS = [
    {name: 'sku'}, {name: 'mpn'}, {name: 'ean'}, {name: 'upc'}, {name: 'asin'},
    {name: 'barcode'}, {name: 'search_key'}, {name: 'brand'}, {name: 'category'},
];

// Match-method options (carried over from the campaign competitor screen's
// "Auto Match" radios, which this step replaced).
const MATCH_METHODS = [
    {value: 'sku', labelKey: 'product.sku'},
    {value: 'mpn', labelKey: 'product.mpn'},
    {value: 'ean', labelKey: 'product.ean'},
    {value: 'upc', labelKey: 'product.upc'},
    {value: 'asin', labelKey: 'product.asin'},
    {value: 'barcode', labelKey: 'product.barcode'},
    {value: 'name_exact', labelKey: 'suggestion.name_exact'},
    {value: 'name_in', labelKey: 'suggestion.name_in'},
    {value: 'match_score', labelKey: 'suggestion.match_score'},
];

// Manual-match target: mirrors the automatic path's supplier pass
// (queue_consumer_command.match_competitors). Only shown while the
// campaign's supplier module is active; the backend default ('competitors')
// matches today's behaviour when it is off.
const MATCH_TARGET_OPTIONS = [
    {value: 'competitors', labelKey: 'onboarding.match.matchTargetSellers'},
    {value: 'suppliers', labelKey: 'onboarding.match.matchTargetSuppliers'},
    {value: 'both', labelKey: 'onboarding.match.matchTargetBoth'},
];

const MatchOnboardingStep = ({t}) => {
    const {toast, setError} = React.useContext(AppContext);
    const {selectedCampaign} = React.useContext(CampaignContext);
    const campaignId = selectedCampaign?.id;
    // The supplier module is a per-campaign opt-in; the target selector on the
    // two manual matching dialogs only exists while it is on (see
    // ProductBulkOperations.js for the same gating convention).
    const supplierModuleActive = !!selectedCampaign?.settings?.supplier_module?.is_active;

    const [showAutoMatch, setShowAutoMatch] = useState(false);
    const [matchMethod, setMatchMethod] = useState(null);
    const [minMatchScore, setMinMatchScore] = useState(70);
    const [matchTarget, setMatchTarget] = useState('competitors');
    const [running, setRunning] = useState(false);
    const [runningOtherCodes, setRunningOtherCodes] = useState(false);
    const [otherCodesTarget, setOtherCodesTarget] = useState('competitors');
    const [showOtherCodesConfirm, setShowOtherCodesConfirm] = useState(false);

    // Site-based selected-fields matching: per-site field selection, each site's
    // fields scope only that site's suggestions.
    const [showSelectedFields, setShowSelectedFields] = useState(false);
    const [loadingFields, setLoadingFields] = useState(false);
    const [fieldOptions, setFieldOptions] = useState(BASE_FIELDS);
    const [matchScope, setMatchScope] = useState('all'); // 'all' | 'per_site'
    const [allFields, setAllFields] = useState([]); // field names applied to all sites
    const [matchSites, setMatchSites] = useState([]);
    const [fieldsBySite, setFieldsBySite] = useState({}); // url -> [field names]
    const [runningSelectedFields, setRunningSelectedFields] = useState(false);

    const setSiteFields = (url, names) => setFieldsBySite(prev => ({...prev, [url]: names}));
    const selectedSiteCount = matchSites.filter(s => (fieldsBySite[s.url] || []).length > 0).length;
    const scopeOptions = [
        {label: t('onboarding.match.scopeAll'), value: 'all'},
        {label: t('onboarding.match.scopePerSite'), value: 'per_site'},
    ];
    const runDisabled = runningSelectedFields || (matchScope === 'all' ? allFields.length === 0 : selectedSiteCount === 0);

    // Matching result (before/after impact) from the onboarding match summary.
    const [metrics, setMetrics] = useState(null);       // current / after
    const [metricsBefore, setMetricsBefore] = useState(null);
    const [lastAction, setLastAction] = useState(null);
    const [metricsLoading, setMetricsLoading] = useState(false);
    const [showDetail, setShowDetail] = useState(false);
    const [detailRows, setDetailRows] = useState([]);
    const [detailLoading, setDetailLoading] = useState(false);

    const openDetail = () => {
        setShowDetail(true);
        setDetailLoading(true);
        productSuggestionService.getStatistics(campaignId)
            .then(rows => setDetailRows(rows || [])).catch(() => {}).finally(() => setDetailLoading(false));
    };

    const fetchMetrics = () => onboardingService.getStatus(campaignId).then(status => {
        const s = (status?.steps || []).find(x => x.key === 'match')?.summary || {};
        return {
            total: s.total || 0,
            matched: s.auto_matched || 0,
            unmatched: s.unmatched || 0,
            pending: s.pending_review || 0,
        };
    });

    useEffect(() => {
        if (!campaignId) return;
        setMetricsLoading(true);
        fetchMetrics().then(m => setMetrics(m)).catch(() => {}).finally(() => setMetricsLoading(false));
    }, [campaignId]); // eslint-disable-line react-hooks/exhaustive-deps

    // After an action: snapshot the pre-action metrics as "before", refetch "after".
    const recordResult = (label) => {
        const before = metrics;
        return fetchMetrics().then(after => {
            setMetricsBefore(before);
            setMetrics(after);
            setLastAction(label);
        }).catch(() => {});
    };

    // Bulk match/unmatch by score dialog state
    const [showMatchOps, setShowMatchOps] = useState(false);
    const [matchOpsAction, setMatchOpsAction] = useState('match');
    const [matchOpsScore, setMatchOpsScore] = useState(70);
    const [matchOpsDomains, setMatchOpsDomains] = useState([]);
    const [matchOpsDomainOptions, setMatchOpsDomainOptions] = useState([]);
    const [matchOpsPreview, setMatchOpsPreview] = useState(null);
    const [matchOpsAffected, setMatchOpsAffected] = useState([]);
    const [matchOpsBusy, setMatchOpsBusy] = useState(false);

    // Recompute-scores dialog: scope = all vs only suggestions missing a new score.
    const [showRecompute, setShowRecompute] = useState(false);
    const [recomputeOnlyMissing, setRecomputeOnlyMissing] = useState(true);

    // Deep link to a product's Suggestions tab, filtered to only the affected rows.
    const affectedLink = (p) =>
        "/#/productdetail?id=" + p.id + "&tab=suggestions&affected=" +
        encodeURIComponent(btoa(JSON.stringify(p.suggestion_urls || [])));

    const openMatchOpsDialog = () => {
        setMatchOpsAction('match');
        setMatchOpsScore(70);
        setMatchOpsDomains([]);
        setMatchOpsDomainOptions([]);
        setMatchOpsPreview(null); setMatchOpsAffected([]);
        setShowMatchOps(true);
        campaignService.getCampaignCompetitors(campaignId, false).then(competitors => {
            const seen = new Set();
            const opts = [];
            (competitors || []).forEach(c => {
                const host = getHostName(c.url);
                if (host && !seen.has(host)) {
                    seen.add(host);
                    opts.push({label: host, value: host});
                }
            });
            setMatchOpsDomainOptions(opts);
        }).catch(e => {
            setError(e);
            setError(null);
        });
    };

    const runMatchOpsPreview = () => {
        setMatchOpsBusy(true);
        productMatchingService.bulkMatchUnmatchByScore(campaignId, matchOpsScore, matchOpsAction, matchOpsDomains, true)
            .then(data => { setMatchOpsPreview(data.count); setMatchOpsAffected(data.affected_products || []); })
            .catch(e => { setError(e); setError(null); })
            .finally(() => setMatchOpsBusy(false));
    };

    const runMatchOpsConfirm = () => {
        setMatchOpsBusy(true);
        productMatchingService.bulkMatchUnmatchByScore(campaignId, matchOpsScore, matchOpsAction, matchOpsDomains, false)
            .then(() => {
                toast.current.show({severity: 'success', summary: t('onboarding.match.matchOps.title'), detail: t('message_detail.successful')});
                setShowMatchOps(false);
                setMatchOpsPreview(null); setMatchOpsAffected([]);
                setMatchOpsAction('match');
                setMatchOpsScore(70);
                setMatchOpsDomains([]);
                recordResult(t('onboarding.match.matchOps.title'));
            })
            .catch(e => { setError(e); setError(null); })
            .finally(() => setMatchOpsBusy(false));
    };

    // Excel-based matching (download template / upload filled file).
    const [showExcel, setShowExcel] = useState(false);
    const [loadingTemplate, setLoadingTemplate] = useState(false);
    const [uploadingExcel, setUploadingExcel] = useState(false);

    // Catalog matching: copy competitors from a source campaign by product code.
    const [showCatalog, setShowCatalog] = useState(false);
    const [catalogCampaigns, setCatalogCampaigns] = useState([]);
    const [sourceCampaignId, setSourceCampaignId] = useState(null);
    const [includedOwner, setIncludedOwner] = useState(false);
    const [loadingCampaigns, setLoadingCampaigns] = useState(false);
    const [runningCatalog, setRunningCatalog] = useState(false);

    // Cross-campaign seller matching: pull sellers from code-matched products
    // in the user's other campaigns (queued background job). Campaign and
    // site selections both default to "all selected".
    const [showCrossCampaign, setShowCrossCampaign] = useState(false);
    const [crossCampaignOptions, setCrossCampaignOptions] = useState([]);
    const [crossCampaignIds, setCrossCampaignIds] = useState([]);
    const [crossHostOptions, setCrossHostOptions] = useState([]);
    const [crossSelectedHosts, setCrossSelectedHosts] = useState([]);
    const [crossLoading, setCrossLoading] = useState(false);
    const [crossSitesLoading, setCrossSitesLoading] = useState(false);
    const [crossQueueing, setCrossQueueing] = useState(false);
    const crossSitesRequest = useRef(0);

    const openCatalog = () => {
        setSourceCampaignId(null);
        setIncludedOwner(false);
        setShowCatalog(true);
        setLoadingCampaigns(true);
        campaignService.getCampaigns().then(list => {
            const options = (list || [])
                .filter(c => c.id !== campaignId)
                .map(c => ({label: c.name, value: c.id}))
                .sort((a, b) => (a.label || '').localeCompare(b.label || ''));
            setCatalogCampaigns(options);
        }).catch(error => {
            setError(error);
            setError(null);
        }).finally(() => setLoadingCampaigns(false));
    };

    const runMatchFromCatalog = () => {
        if (!sourceCampaignId) {
            toast.current.show({severity: 'error', summary: t('onboarding.match.catalogSelectSource'), detail: ''});
            return;
        }
        setRunningCatalog(true);
        productMatchingService.matchProductsFromOtherCampaign(campaignId, sourceCampaignId, includedOwner).then(() => {
            setShowCatalog(false);
            toast.current.show({
                severity: 'success',
                summary: t('onboarding.match.catalogTitle'),
                detail: t('message_detail.successful'),
            });
            recordResult(t('onboarding.match.catalogTitle'));
        }).catch(error => {
            setError(error);
            setError(null);
        }).finally(() => setRunningCatalog(false));
    };

    // Union of hosts across the selected source campaigns; reloads whenever
    // the campaign selection changes. Deselected hosts are excluded from
    // matching entirely (backend receives the selected list).
    const loadCrossSites = (campaignIds) => {
        const requestId = ++crossSitesRequest.current;
        if (!campaignIds || campaignIds.length === 0) {
            setCrossHostOptions([]);
            setCrossSelectedHosts([]);
            // Any in-flight request is now stale and won't reset the flag itself.
            setCrossSitesLoading(false);
            return;
        }
        setCrossSitesLoading(true);
        Promise.all(campaignIds.map(id => campaignService.getCampaignCompetitors(id, false).catch(() => [])))
            .then(lists => {
                // A newer selection superseded this request; drop the stale response.
                if (requestId !== crossSitesRequest.current) return;
                const seen = new Set();
                const hosts = [];
                lists.flat().forEach(site => {
                    const host = getHostName(site.url);
                    if (host && !seen.has(host)) {
                        seen.add(host);
                        hosts.push(host);
                    }
                });
                hosts.sort();
                setCrossHostOptions(hosts.map(h => ({label: h, value: h})));
                setCrossSelectedHosts(hosts);
            })
            .finally(() => {
                if (requestId === crossSitesRequest.current) setCrossSitesLoading(false);
            });
    };

    const openCrossCampaign = () => {
        setShowCrossCampaign(true);
        setCrossLoading(true);
        setCrossHostOptions([]);
        setCrossSelectedHosts([]);
        campaignService.getCampaigns().then(list => {
            const options = (list || [])
                .filter(c => c.id !== campaignId)
                .map(c => ({label: c.name, value: c.id}))
                .sort((a, b) => (a.label || '').localeCompare(b.label || ''));
            setCrossCampaignOptions(options);
            const ids = options.map(o => o.value);
            setCrossCampaignIds(ids);
            loadCrossSites(ids);
        }).catch(error => {
            setError(error);
            setError(null);
        }).finally(() => setCrossLoading(false));
    };

    const onCrossCampaignsChange = (ids) => {
        setCrossCampaignIds(ids);
        loadCrossSites(ids);
    };

    const runCrossCampaignMatch = () => {
        setCrossQueueing(true);
        queueService.createQueueItem('match_sellers_from_other_campaigns', {
            campaign_id: campaignId,
            source_campaign_ids: crossCampaignIds,
            selected_hosts: crossSelectedHosts,
        }).then(() => {
            setShowCrossCampaign(false);
            toast.current.show({
                severity: 'success',
                summary: t('onboarding.match.crossCampaign.title'),
                detail: t('message_detail.request_queued'),
            });
        }).catch(error => {
            setError(error);
            setError(null);
        }).finally(() => setCrossQueueing(false));
    };

    const downloadExcelTemplate = () => {
        setLoadingTemplate(true);
        templateService.exportProductSuggestionsMatchingTemplate().then((response) => {
            exportFile(response, "match_product_suggestions_template.xlsx");
        }).catch(error => {
            setError(error);
            setError(null);
        }).finally(() => setLoadingTemplate(false));
    };

    const uploadExcel = ({files}) => {
        const [file] = files;
        // No options.clear(): the busy flag below remounts the FileUpload empty (see ExcelOps).
        setUploadingExcel(true);
        productExcelImportService.matchProductSuggestions(campaignId, file).then(() => {
            toast.current.show({
                severity: 'success',
                summary: t('actions.match_product_suggestions'),
                detail: t('message_detail.successful'),
            });
            recordResult(t('actions.match_product_suggestions'));
        }).catch(error => {
            setError(error);
            setError(null);
        }).finally(() => setUploadingExcel(false));
    };

    const openSelectedFields = () => {
        setShowSelectedFields(true);
        setLoadingFields(true);
        // Load field options (codes + special fields) and the campaign's sites.
        Promise.all([
            specialFieldService.getSpecialFields().catch(() => []),
            campaignService.getCampaignCompetitors(campaignId).catch(() => []),
        ]).then(([special, sites]) => {
            setFieldOptions(BASE_FIELDS.concat(special || []));
            const list = sites || [];
            setMatchSites(list);
            // Seed from any existing per-site config (match_fields_based_url).
            const job = (selectedCampaign?.settings && selectedCampaign.settings.site_search_job) || {};
            const map = job.match_fields_based_url || {};
            const seeded = {};
            list.forEach(s => {
                const cfg = map[s.url];
                seeded[s.url] = (cfg && cfg.fields ? cfg.fields : []).map(f => (f.name || f));
            });
            setFieldsBySite(seeded);
            setLoadingFields(false);
        }).catch(error => {
            setError(error);
            setError(null);
            setLoadingFields(false);
        });
    };

    const onSelectedFieldsSuccess = () => {
        setShowSelectedFields(false);
        toast.current.show({
            severity: 'success',
            summary: t('actions.match_suggestions_using_selected_fields'),
            detail: t('message_detail.successful'),
        });
        recordResult(t('actions.match_suggestions_using_selected_fields'));
    };

    const runMatchUsingSelectedFields = () => {
        // "All sites" mode: one common field set applied across every site.
        if (matchScope === 'all') {
            if (allFields.length === 0) {
                toast.current.show({severity: 'error', summary: t('onboarding.match.selectedFieldsEmpty'), detail: ''});
                return;
            }
            setRunningSelectedFields(true);
            productMatchingService.matchCompetitorsUsingSelectedFields(campaignId, allFields.map(n => ({name: n})))
                .then(onSelectedFieldsSuccess)
                .catch(error => { setError(error); setError(null); })
                .finally(() => setRunningSelectedFields(false));
            return;
        }
        // "Per site" mode: build match_fields_based_url from sites with fields.
        const map = {};
        matchSites.forEach(s => {
            const names = fieldsBySite[s.url] || [];
            if (names.length > 0)
                map[s.url] = {selected: true, fields: names.map(n => ({name: n}))};
        });
        if (Object.keys(map).length === 0) {
            toast.current.show({severity: 'error', summary: t('onboarding.match.selectedFieldsEmpty'), detail: ''});
            return;
        }
        setRunningSelectedFields(true);
        productMatchingService.matchCompetitorsUsingMatchFields(campaignId, map, null)
            .then(onSelectedFieldsSuccess)
            .catch(error => { setError(error); setError(null); })
            .finally(() => setRunningSelectedFields(false));
    };

    const runAutoMatch = () => {
        if (!matchMethod) {
            toast.current.show({severity: 'error', summary: t('suggestion.select_match_method'), detail: ''});
            return;
        }
        setRunning(true);
        productService.autoMatchAllSuggestions(campaignId, matchMethod, minMatchScore, matchTarget).then(() => {
            setShowAutoMatch(false);
            toast.current.show({
                severity: 'success',
                summary: t('actions.auto_match_suggestions'),
                detail: t('message_detail.successful'),
            });
            recordResult(t('actions.auto_match_suggestions'));
        }).catch(error => {
            setError(error);
            setError(null);
        }).finally(() => setRunning(false));
    };

    const runMatchUsingOtherCodes = () => {
        setShowOtherCodesConfirm(false);
        setRunningOtherCodes(true);
        productMatchingService.matchCompetitorsUsingOtherCodes(campaignId, otherCodesTarget).then(() => {
            toast.current.show({
                severity: 'success',
                summary: t('actions.match_suggestions_using_other_codes'),
                detail: t('message_detail.successful'),
            });
            recordResult(t('actions.match_suggestions_using_other_codes'));
        }).catch(error => {
            setError(error);
            setError(null);
        }).finally(() => setRunningOtherCodes(false));
    };

    const recomputeScoresCampaign = (onlyMissing) => {
        queueService.createQueueItem('compute_match_scores', { campaign_id: campaignId, only_missing: onlyMissing })
            .then(() => toast.current.show({
                severity: 'success',
                summary: t('suggestion.recompute_scores'),
                detail: t('message_detail.request_queued'),
            }))
            .catch(error => {
                setError(error);
                setError(null);
            })
            .finally(() => setShowRecompute(false));
    };

    // Learn the pattern abbreviation -> full-name dictionary from matched pairs
    // across ALL of this user's campaigns (improves cross-language/model matching).
    const buildPatternAliases = () => {
        const param = selectedCampaign?.user_id
            ? { user_id: selectedCampaign.user_id }
            : { campaign_id: campaignId };
        queueService.createQueueItem('build_pattern_aliases', param)
            .then(() => toast.current.show({
                severity: 'success',
                summary: t('suggestion.build_pattern_aliases'),
                detail: t('message_detail.request_queued'),
            }))
            .catch(error => {
                setError(error);
                setError(null);
            });
    };

    return (
        <div>
            <h2 style={{margin: '0 0 6px', display: 'flex', alignItems: 'center', gap: 10}}>
                <i className="pi pi-link"/> {t('onboarding.step.match.label', 'Eşleştirme')}
            </h2>
            <p style={{color: '#6b7280', maxWidth: 640, marginTop: 0}}>{t('onboarding.step.match.desc')}</p>

            {isAdmin() &&
            <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 16, marginTop: 8}}>
                <div style={{border: '1px solid #e5e7eb', borderRadius: 10, padding: 16, background: '#fff'}}>
                    <div style={{fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4}}>
                        <i className="pi pi-bolt" style={{color: '#16a34a'}}/> {t('actions.auto_match_suggestions')}
                    </div>
                    <p style={{color: '#6b7280', fontSize: '.85rem', margin: '0 0 12px'}}>{t('onboarding.match.autoMatchHint')}</p>
                    <Button label={t('actions.auto_match_suggestions')} icon="pi pi-bolt"
                            className="p-button-success" onClick={() => setShowAutoMatch(true)}/>
                </div>

                <div style={{border: '1px solid #e5e7eb', borderRadius: 10, padding: 16, background: '#fff'}}>
                    <div style={{fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4}}>
                        <i className="pi pi-sitemap" style={{color: '#7c3aed'}}/> {t('actions.match_suggestions_using_other_codes')}
                    </div>
                    <p style={{color: '#6b7280', fontSize: '.85rem', margin: '0 0 12px'}}>{t('onboarding.match.otherCodesHint')}</p>
                    <Button label={t('actions.match_suggestions_using_other_codes')} icon="pi pi-sitemap"
                            className="p-button-help" loading={runningOtherCodes} disabled={runningOtherCodes}
                            onClick={() => setShowOtherCodesConfirm(true)}/>
                </div>

                <div style={{border: '1px solid #e5e7eb', borderRadius: 10, padding: 16, background: '#fff'}}>
                    <div style={{fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4}}>
                        <i className="pi pi-filter" style={{color: '#0891b2'}}/> {t('actions.match_suggestions_using_selected_fields')}
                    </div>
                    <p style={{color: '#6b7280', fontSize: '.85rem', margin: '0 0 12px'}}>{t('onboarding.match.selectedFieldsHint')}</p>
                    <Button label={t('actions.match_suggestions_using_selected_fields')} icon="pi pi-filter"
                            className="p-button-info" onClick={openSelectedFields}/>
                </div>

                <div style={{border: '1px solid #e5e7eb', borderRadius: 10, padding: 16, background: '#fff'}}>
                    <div style={{fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4}}>
                        <i className="pi pi-file-excel" style={{color: '#15803d'}}/> {t('actions.match_product_suggestions')}
                    </div>
                    <p style={{color: '#6b7280', fontSize: '.85rem', margin: '0 0 12px'}}>{t('onboarding.match.excelHint')}</p>
                    <Button label={t('actions.match_product_suggestions')} icon="pi pi-file-excel"
                            className="p-button-success p-button-outlined" onClick={() => setShowExcel(true)}/>
                </div>

                <div style={{border: '1px solid #e5e7eb', borderRadius: 10, padding: 16, background: '#fff'}}>
                    <div style={{fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4}}>
                        <i className="pi pi-book" style={{color: '#db2777'}}/> {t('onboarding.match.catalogTitle')}
                    </div>
                    <p style={{color: '#6b7280', fontSize: '.85rem', margin: '0 0 12px'}}>{t('onboarding.match.catalogHint')}</p>
                    <Button label={t('onboarding.match.catalogTitle')} icon="pi pi-book"
                            className="p-button-warning" onClick={openCatalog}/>
                </div>

                <div style={{border: '1px solid #e5e7eb', borderRadius: 10, padding: 16, background: '#fff'}}>
                    <div style={{fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4}}>
                        <i className="pi pi-refresh" style={{color: '#0d9488'}}/> {t('suggestion.recompute_scores')}
                    </div>
                    <p style={{color: '#6b7280', fontSize: '.85rem', margin: '0 0 12px'}}>{t('onboarding.match.recomputeHint')}</p>
                    <Button label={t('suggestion.recompute_scores')} icon="pi pi-refresh"
                            className="p-button-secondary"
                            onClick={() => { setRecomputeOnlyMissing(true); setShowRecompute(true); }}/>
                </div>

                <div style={{border: '1px solid #e5e7eb', borderRadius: 10, padding: 16, background: '#fff'}}>
                    <div style={{fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4}}>
                        <i className="pi pi-sliders-h" style={{color: '#7c3aed'}}/> {t('onboarding.match.matchOps.title')}
                    </div>
                    <p style={{color: '#6b7280', fontSize: '.85rem', margin: '0 0 12px'}}>{t('onboarding.match.matchOps.hint')}</p>
                    <Button label={t('onboarding.match.matchOps.title')} icon="pi pi-sliders-h"
                            className="p-button-help" onClick={openMatchOpsDialog}/>
                </div>

                <div style={{border: '1px solid #e5e7eb', borderRadius: 10, padding: 16, background: '#fff'}}>
                    <div style={{fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4}}>
                        <i className="pi pi-clone" style={{color: '#ea580c'}}/> {t('onboarding.match.crossCampaign.title')}
                    </div>
                    <p style={{color: '#6b7280', fontSize: '.85rem', margin: '0 0 12px'}}>{t('onboarding.match.crossCampaign.hint')}</p>
                    <Button label={t('onboarding.match.crossCampaign.title')} icon="pi pi-clone"
                            className="p-button-warning p-button-outlined" onClick={openCrossCampaign}/>
                </div>
            </div>}

            {/* Matching result: before/after impact of the last action. */}
            <div style={{marginTop: 20, border: '1px solid #e5e7eb', borderRadius: 10, padding: 16, background: '#fff'}}>
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, gap: 12, flexWrap: 'wrap'}}>
                    <b style={{display: 'flex', alignItems: 'center', gap: 8}}>
                        <i className="pi pi-chart-bar" style={{color: '#2563eb'}}/> {t('onboarding.match.resultTitle')}
                    </b>
                    <span style={{display: 'flex', alignItems: 'center', gap: 10}}>
                        {lastAction && <small style={{color: '#6b7280'}}>{t('onboarding.match.lastAction')}: <b>{lastAction}</b></small>}
                        <Button icon="pi pi-refresh" className="p-button-text p-button-sm" loading={metricsLoading}
                                label={t('actions.refresh', 'Yenile')}
                                onClick={() => { setMetricsLoading(true); fetchMetrics().then(m => setMetrics(m)).catch(() => {}).finally(() => setMetricsLoading(false)); }}/>
                        <Button icon="pi pi-table" className="p-button-text p-button-sm"
                                label={t('onboarding.match.detailResult', 'Detay Sonuç')} onClick={openDetail}/>
                    </span>
                </div>
                {metricsLoading && !metrics
                    ? <div style={{textAlign: 'center', padding: 16}}><Loading/></div>
                    : !metrics
                        ? <Message severity="info" text={t('onboarding.match.resultEmpty')}/>
                        : (() => {
                            const rows = [
                                {key: 'matched', label: t('onboarding.match.metricMatched'), good: 'up'},
                                {key: 'pending', label: t('onboarding.match.metricPending'), good: 'down'},
                                {key: 'unmatched', label: t('onboarding.match.metricUnmatched'), good: null},
                                {key: 'total', label: t('onboarding.match.metricTotal'), good: null},
                            ];
                            const delta = (k) => metricsBefore ? metrics[k] - metricsBefore[k] : null;
                            const deltaColor = (d, good) => d === 0 || d === null ? '#9ca3af'
                                : good === null ? '#374151'
                                : ((good === 'up') === (d > 0) ? '#16a34a' : '#dc2626');
                            return <table style={{width: '100%', borderCollapse: 'collapse', fontSize: '.9rem'}}>
                                <thead>
                                    <tr style={{textAlign: 'left', color: '#6b7280'}}>
                                        <th style={{padding: '6px 8px'}}>{t('onboarding.match.metric')}</th>
                                        {metricsBefore && <th style={{padding: '6px 8px', textAlign: 'right'}}>{t('onboarding.match.before')}</th>}
                                        <th style={{padding: '6px 8px', textAlign: 'right'}}>{metricsBefore ? t('onboarding.match.after') : t('onboarding.match.current')}</th>
                                        {metricsBefore && <th style={{padding: '6px 8px', textAlign: 'right'}}>{t('onboarding.match.change')}</th>}
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map(r => {
                                        const d = delta(r.key);
                                        return <tr key={r.key} style={{borderTop: '1px solid #f1f5f9'}}>
                                            <td style={{padding: '6px 8px'}}>{r.label}</td>
                                            {metricsBefore && <td style={{padding: '6px 8px', textAlign: 'right'}}>{metricsBefore[r.key]}</td>}
                                            <td style={{padding: '6px 8px', textAlign: 'right', fontWeight: 600}}>{metrics[r.key]}</td>
                                            {metricsBefore && <td style={{padding: '6px 8px', textAlign: 'right', fontWeight: 700, color: deltaColor(d, r.good)}}>
                                                {d > 0 ? `+${d}` : d}
                                            </td>}
                                        </tr>;
                                    })}
                                </tbody>
                            </table>;
                        })()}
            </div>

            {/* Detail result: per-URL suggestion statistics */}
            <Dialog header={t('onboarding.match.detailResult', 'Detay Sonuç')} visible={showDetail}
                    style={{width: '900px', maxWidth: '96vw'}} modal onHide={() => setShowDetail(false)}>
                {detailLoading
                    ? <div style={{textAlign: 'center', padding: 24}}><Loading/></div>
                    : <DataTable value={detailRows} dataKey="url" responsiveLayout="scroll"
                                 paginator={detailRows.length > 12} rows={12}
                                 emptyMessage={t('onboarding.match.detailEmpty', 'Kayıt yok.')}>
                        <Column field="url" sortable header={t('suggestion.url', 'URL')}
                                body={(row) => <a href={row.url} target="_blank" rel="noopener noreferrer">{row.url}</a>}/>
                        <Column field="total_count" header={t('suggestion.total_count')} style={{width: '9em'}}/>
                        <Column field="total_matched_count" header={t('suggestion.total_matched_count')} style={{width: '9em'}}/>
                        <Column field="total_unmatched_count" header={t('suggestion.total_unmatched_count')} style={{width: '9em'}}/>
                        <Column field="total_undecided_count" header={t('suggestion.total_undecided_count')} style={{width: '9em'}}/>
                        <Column field="total_suggested_product_count" header={t('suggestion.total_suggested_product_count')} style={{width: '9em'}}/>
                        <Column field="total_not_suggested_product_count" header={t('suggestion.total_not_suggested_product_count')} style={{width: '9em'}}/>
                      </DataTable>}
            </Dialog>

            {/* Confirm: this button runs immediately (no settings popup). */}
            <Dialog header={t('actions.match_suggestions_using_other_codes')} visible={showOtherCodesConfirm}
                    style={{width: '460px', maxWidth: '95vw'}} modal
                    onHide={() => !runningOtherCodes && setShowOtherCodesConfirm(false)}
                    footer={
                        <div>
                            <Button label={t('actions.cancel')} className="p-button-text"
                                    disabled={runningOtherCodes} onClick={() => setShowOtherCodesConfirm(false)}/>
                            <Button label={t('onboarding.sites.run')} icon="pi pi-play"
                                    loading={runningOtherCodes} onClick={runMatchUsingOtherCodes}/>
                        </div>
                    }>
                <span>{t('onboarding.match.otherCodesConfirm')}</span>
                {supplierModuleActive &&
                <div style={{marginTop: '.75em'}}>
                    <label style={{display: 'block', marginBottom: '.35em', color: '#6b7280', fontSize: '.85rem'}}>
                        {t('onboarding.match.matchTarget')}
                    </label>
                    <SelectButton value={otherCodesTarget}
                                  options={MATCH_TARGET_OPTIONS.map(o => ({label: t(o.labelKey), value: o.value}))}
                                  onChange={(e) => { if (e.value) setOtherCodesTarget(e.value); }}/>
                </div>}
            </Dialog>

            {/* Popup: pick a source ("catalog") campaign + run product matching. */}
            <Dialog header={t('onboarding.match.catalogTitle')} visible={showCatalog}
                    style={{width: '520px', maxWidth: '95vw'}} modal
                    onHide={() => !runningCatalog && setShowCatalog(false)}
                    footer={
                        <div>
                            <Button label={t('actions.cancel')} className="p-button-text"
                                    disabled={runningCatalog} onClick={() => setShowCatalog(false)}/>
                            <Button label={t('onboarding.sites.run')} icon="pi pi-play"
                                    loading={runningCatalog} disabled={runningCatalog || !sourceCampaignId}
                                    onClick={runMatchFromCatalog}/>
                        </div>
                    }>
                <small style={{display: 'block', color: '#6b7280', marginBottom: '1em'}}>
                    {t('onboarding.match.catalogPopupHint')}
                </small>
                {loadingCampaigns
                    ? <div style={{textAlign: 'center', padding: 16}}><Loading/></div>
                    : <div style={{display: 'flex', flexDirection: 'column', gap: '1em'}}>
                        <div>
                            <label style={{display: 'block', marginBottom: '.35em', fontWeight: 600}}>{t('campaign.other_campaigns')}</label>
                            <Dropdown value={sourceCampaignId} options={catalogCampaigns} filter
                                      placeholder={t('campaign.other_campaigns')} style={{width: '100%'}}
                                      onChange={(e) => setSourceCampaignId(e.value)}/>
                        </div>
                        <div>
                            <div style={{display: 'flex', alignItems: 'center', gap: '.5em'}}>
                                <Checkbox inputId="catalog_included_owner" checked={includedOwner}
                                          onChange={(e) => setIncludedOwner(e.checked)}/>
                                <label htmlFor="catalog_included_owner" style={{margin: 0}}>{t('onboarding.match.catalogIncludeOwner')}</label>
                            </div>
                            <small style={{display: 'block', color: '#6b7280', marginTop: '.35em', marginLeft: '1.6em'}}>
                                {t('onboarding.match.catalogIncludeOwnerHint')}
                            </small>
                        </div>
                    </div>}
            </Dialog>

            {/* Popup: pick source campaigns + sites; queues a background job
                that pulls sellers from code-matched products. */}
            <Dialog header={t('onboarding.match.crossCampaign.title')} visible={showCrossCampaign}
                    style={{width: '560px', maxWidth: '95vw'}} modal
                    onHide={() => !crossQueueing && setShowCrossCampaign(false)}
                    footer={
                        <div>
                            <Button label={t('actions.cancel')} className="p-button-text"
                                    disabled={crossQueueing} onClick={() => setShowCrossCampaign(false)}/>
                            <Button label={t('onboarding.sites.run')} icon="pi pi-play"
                                    loading={crossQueueing}
                                    disabled={crossQueueing || crossSitesLoading
                                        || crossCampaignIds.length === 0 || crossSelectedHosts.length === 0}
                                    onClick={runCrossCampaignMatch}/>
                        </div>
                    }>
                <small style={{display: 'block', color: '#6b7280', marginBottom: '1em'}}>
                    {t('onboarding.match.crossCampaign.popupHint')}
                </small>
                {crossLoading
                    ? <div style={{textAlign: 'center', padding: 16}}><Loading/></div>
                    : <div style={{display: 'flex', flexDirection: 'column', gap: '1em'}}>
                        <div>
                            <label style={{display: 'block', marginBottom: '.35em', fontWeight: 600}}>{t('campaign.other_campaigns')}</label>
                            <MultiSelect value={crossCampaignIds} options={crossCampaignOptions}
                                         display="chip" filter style={{width: '100%'}}
                                         placeholder={t('campaign.other_campaigns')}
                                         onChange={(e) => onCrossCampaignsChange(e.value || [])}/>
                        </div>
                        <div>
                            <label style={{display: 'block', marginBottom: '.35em', fontWeight: 600}}>{t('onboarding.match.crossCampaign.sites')}</label>
                            {crossSitesLoading
                                ? <Loading width="25px" height="25px"/>
                                : crossHostOptions.length === 0
                                    ? <Message severity="info" text={t('onboarding.match.crossCampaign.sitesEmpty')}/>
                                    : <MultiSelect value={crossSelectedHosts} options={crossHostOptions}
                                                   display="chip" filter style={{width: '100%'}}
                                                   placeholder={t('onboarding.match.crossCampaign.sites')}
                                                   onChange={(e) => setCrossSelectedHosts(e.value || [])}/>}
                            <small style={{display: 'block', color: '#6b7280', marginTop: '.35em'}}>
                                {t('onboarding.match.crossCampaign.sitesHint')}
                            </small>
                        </div>
                    </div>}
            </Dialog>

            {/* Popup: per-site field selection; each site's fields scope only
                that site's suggestions. */}
            <Dialog header={t('actions.match_suggestions_using_selected_fields')} visible={showSelectedFields}
                    style={{width: '760px', maxWidth: '95vw'}} modal
                    onHide={() => !runningSelectedFields && setShowSelectedFields(false)}
                    footer={
                        <div>
                            <Button label={t('actions.cancel')} className="p-button-text"
                                    disabled={runningSelectedFields} onClick={() => setShowSelectedFields(false)}/>
                            <Button label={t('onboarding.sites.run') + (matchScope === 'per_site' && selectedSiteCount ? ` (${selectedSiteCount})` : '')}
                                    icon="pi pi-play"
                                    loading={runningSelectedFields} disabled={runDisabled}
                                    onClick={runMatchUsingSelectedFields}/>
                        </div>
                    }>
                <div style={{marginBottom: '.75em'}}>
                    <SelectButton value={matchScope} options={scopeOptions}
                                  onChange={(e) => { if (e.value) setMatchScope(e.value); }}/>
                </div>
                <small style={{display: 'block', color: '#6b7280', marginBottom: '.75em'}}>
                    {matchScope === 'all'
                        ? t('onboarding.match.scopeAllHint')
                        : t('onboarding.match.selectedFieldsPopupHint')}
                </small>
                {loadingFields
                    ? <div style={{textAlign: 'center', padding: 24}}><Loading/></div>
                    : matchScope === 'all'
                        ? <div>
                            <label style={{display: 'block', marginBottom: '.35em', fontWeight: 600}}>{t('suggestion.match_fields')}</label>
                            <MultiSelect value={allFields} options={fieldOptions}
                                         optionLabel="name" optionValue="name" display="chip" filter
                                         placeholder={t('suggestion.match_fields')} style={{width: '100%'}}
                                         onChange={(e) => setAllFields(e.value || [])}/>
                          </div>
                        : matchSites.length === 0
                            ? <Message severity="info" text={t('onboarding.sites.empty')}/>
                            : <DataTable value={matchSites} dataKey="url" responsiveLayout="scroll"
                                         paginator={matchSites.length > 10} rows={10}>
                                <Column header={t('onboarding.sites.site')}
                                        body={(row) => <a href={row.url} target="_blank" rel="noopener noreferrer">{row.url}</a>}/>
                                <Column header={t('suggestion.match_fields')} style={{width: '20em'}}
                                        body={(row) =>
                                            <MultiSelect value={fieldsBySite[row.url] || []} options={fieldOptions}
                                                         optionLabel="name" optionValue="name" display="chip" filter
                                                         placeholder={t('suggestion.match_fields')} style={{width: '100%'}}
                                                         onChange={(e) => setSiteFields(row.url, e.value || [])}/>}/>
                              </DataTable>}
            </Dialog>

            {/* Popup: Excel template download + filled-file upload. */}
            <Dialog header={t('actions.match_product_suggestions')} visible={showExcel}
                    style={{width: '560px', maxWidth: '95vw'}} modal
                    onHide={() => !uploadingExcel && setShowExcel(false)}
                    footer={<Button label={t('actions.close')} className="p-button-text"
                                    disabled={uploadingExcel} onClick={() => setShowExcel(false)}/>}>
                <small style={{display: 'block', color: '#6b7280', marginBottom: '1em'}}>
                    {t('onboarding.match.excelPopupHint')}
                </small>
                <div style={{display: 'flex', flexDirection: 'column', gap: '1em'}}>
                    <div>
                        <div style={{fontWeight: 600, marginBottom: '.35em'}}>1. {t('actions.download_match_product_suggestions_template')}</div>
                        <Button icon="pi pi-download" className="p-button-outlined p-button-success"
                                label={t('actions.download_match_product_suggestions_template')}
                                loading={loadingTemplate} onClick={downloadExcelTemplate}/>
                    </div>
                    <div>
                        <div style={{fontWeight: 600, marginBottom: '.35em'}}>2. {t('actions.match_product_suggestions')}</div>
                        {uploadingExcel
                            ? <Loading width="25px" height="25px"/>
                            : <FileUpload customUpload mode="basic" auto
                                          accept=".xlsx,.xls"
                                          chooseLabel={t('actions.match_product_suggestions')}
                                          uploadHandler={uploadExcel}/>}
                    </div>
                </div>
            </Dialog>

            {/* Popup: bulk match/unmatch undecided suggestions by score threshold. */}
            <Dialog header={t('onboarding.match.matchOps.title')} visible={showMatchOps}
                    style={{width: '520px', maxWidth: '95vw'}} modal
                    onHide={() => {
                        if (!matchOpsBusy) {
                            setShowMatchOps(false);
                            setMatchOpsPreview(null); setMatchOpsAffected([]);
                            setMatchOpsAction('match');
                            setMatchOpsScore(70);
                            setMatchOpsDomains([]);
                        }
                    }}
                    footer={
                        <div>
                            <Button label={t('actions.cancel')} className="p-button-text"
                                    disabled={matchOpsBusy} onClick={() => setShowMatchOps(false)}/>
                            {matchOpsPreview === null
                                ? <Button label={t('onboarding.cleanup.lowScore.preview')} icon="pi pi-search"
                                          loading={matchOpsBusy} disabled={matchOpsBusy}
                                          onClick={runMatchOpsPreview}/>
                                : <Button label={matchOpsAction === 'match' ? t('onboarding.match.matchOps.match') : t('onboarding.match.matchOps.unmatch')}
                                          icon={matchOpsAction === 'match' ? 'pi pi-check' : 'pi pi-times'}
                                          loading={matchOpsBusy} disabled={matchOpsBusy}
                                          onClick={runMatchOpsConfirm}/>}
                        </div>
                    }>
                <div style={{display: 'flex', flexDirection: 'column', gap: '1em'}}>
                    <div>
                        <SelectButton value={matchOpsAction}
                                      options={[
                                          {label: t('onboarding.match.matchOps.match'), value: 'match'},
                                          {label: t('onboarding.match.matchOps.unmatch'), value: 'unmatch'},
                                      ]}
                                      onChange={(e) => { if (e.value) { setMatchOpsAction(e.value); setMatchOpsPreview(null); setMatchOpsAffected([]); } }}/>
                    </div>
                    <small style={{display: 'block', color: '#6b7280', margin: '4px 0 14px'}}>
                        {matchOpsAction === 'match'
                            ? t('onboarding.match.matchOps.matchDesc')
                            : t('onboarding.match.matchOps.unmatchDesc')}
                    </small>
                    <div>
                        <label style={{display: 'block', marginBottom: '.35em', fontWeight: 600}}>
                            {t('onboarding.match.matchOps.score')}
                        </label>
                        <InputNumber value={matchOpsScore} min={0} max={100} showButtons
                                     inputStyle={{width: 80}}
                                     onValueChange={(e) => { setMatchOpsScore(e.value ?? 70); setMatchOpsPreview(null); setMatchOpsAffected([]); }}/>
                    </div>
                    <div>
                        <label style={{display: 'block', marginBottom: '.35em', fontWeight: 600}}>
                            {t('onboarding.match.matchOps.domains')}
                        </label>
                        <MultiSelect value={matchOpsDomains} options={matchOpsDomainOptions}
                                     display="chip" filter
                                     placeholder={t('onboarding.match.matchOps.domains')}
                                     style={{width: '100%'}}
                                     onChange={(e) => { setMatchOpsDomains(e.value || []); setMatchOpsPreview(null); setMatchOpsAffected([]); }}/>
                    </div>
                    {matchOpsPreview !== null && (
                        <Message severity="info"
                                 text={t('onboarding.match.matchOps.previewCount', {count: matchOpsPreview})}
                                 style={{display: 'block'}}/>
                    )}
                    {matchOpsPreview !== null && matchOpsAffected.length > 0 && (
                        <div>
                            <label style={{display: 'block', marginBottom: '.35em', fontWeight: 600}}>
                                {t('onboarding.affectedProducts')}
                            </label>
                            <div style={{maxHeight: 200, overflowY: 'auto', border: '1px solid #e5e7eb',
                                borderRadius: 8, padding: 8}}>
                                {matchOpsAffected.map(p => (
                                    <div key={p.id} style={{display: 'flex', justifyContent: 'space-between',
                                        gap: 8, padding: '3px 0'}}>
                                        <a href={affectedLink(p)} target="_blank" rel="noopener noreferrer"
                                           style={{overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
                                            {p.name || p.id}
                                        </a>
                                        <span style={{color: '#6b7280', flexShrink: 0}}>{(p.suggestion_urls || []).length}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </Dialog>

            {/* Recompute scores: choose scope (all vs only missing/old-style). */}
            <Dialog header={t('suggestion.recompute_scores')} visible={showRecompute}
                    style={{width: '520px', maxWidth: '95vw'}} modal
                    onHide={() => setShowRecompute(false)}
                    footer={
                        <div>
                            <Button label={t('actions.cancel')} className="p-button-text"
                                    onClick={() => setShowRecompute(false)}/>
                            <Button label={t('suggestion.recompute_scores')} icon="pi pi-refresh"
                                    onClick={() => recomputeScoresCampaign(recomputeOnlyMissing)}/>
                        </div>
                    }>
                <div style={{display: 'flex', flexDirection: 'column', gap: 14}}>
                    <div style={{display: 'flex', alignItems: 'center', gap: 10,
                        paddingBottom: 12, borderBottom: '1px solid #e5e7eb'}}>
                        <Button label={t('suggestion.build_pattern_aliases')} icon="pi pi-book"
                                className="p-button-sm p-button-outlined"
                                onClick={buildPatternAliases}
                                tooltip={t('onboarding.match.buildAliasesHint')}
                                tooltipOptions={{position: 'top'}}/>
                        <small style={{color: '#6b7280'}}>{t('onboarding.match.buildAliasesShort')}</small>
                    </div>
                    <div style={{display: 'flex', gap: 10, alignItems: 'flex-start'}}>
                        <RadioButton inputId="recompute_missing" name="recompute_scope" value={true}
                                     checked={recomputeOnlyMissing === true}
                                     onChange={() => setRecomputeOnlyMissing(true)}/>
                        <label htmlFor="recompute_missing" style={{cursor: 'pointer'}}>
                            <div style={{fontWeight: 600}}>{t('suggestion.recompute_scope.missing')}</div>
                            <small style={{color: '#6b7280'}}>{t('suggestion.recompute_scope.missingHint')}</small>
                        </label>
                    </div>
                    <div style={{display: 'flex', gap: 10, alignItems: 'flex-start'}}>
                        <RadioButton inputId="recompute_all" name="recompute_scope" value={false}
                                     checked={recomputeOnlyMissing === false}
                                     onChange={() => setRecomputeOnlyMissing(false)}/>
                        <label htmlFor="recompute_all" style={{cursor: 'pointer'}}>
                            <div style={{fontWeight: 600}}>{t('suggestion.recompute_scope.all')}</div>
                            <small style={{color: '#6b7280'}}>{t('suggestion.recompute_scope.allHint')}</small>
                        </label>
                    </div>
                </div>
            </Dialog>

            {/* Popup: pick a match method (radio) + run auto-match. */}
            <Dialog header={t('actions.auto_match_suggestions')} visible={showAutoMatch}
                    style={{width: '640px', maxWidth: '95vw'}} modal
                    onHide={() => !running && setShowAutoMatch(false)}
                    footer={
                        <div>
                            <Button label={t('actions.cancel')} className="p-button-text"
                                    disabled={running} onClick={() => setShowAutoMatch(false)}/>
                            <Button label={t('onboarding.sites.run')} icon="pi pi-play"
                                    loading={running} disabled={running || !matchMethod}
                                    onClick={runAutoMatch}/>
                        </div>
                    }>
                <small style={{display: 'block', color: '#6b7280', marginBottom: '.75em'}}>
                    {t('onboarding.match.autoMatchPopupHint')}
                </small>
                <div style={{display: 'flex', flexWrap: 'wrap', gap: '.5em 1.25em', alignItems: 'center'}}>
                    {MATCH_METHODS.map(m =>
                        <span key={m.value} style={{display: 'inline-flex', alignItems: 'center', gap: '.35em'}}>
                            <RadioButton inputId={`match_method_${m.value}`} name="match_method" value={m.value}
                                         checked={matchMethod === m.value} onChange={() => setMatchMethod(m.value)}/>
                            <label htmlFor={`match_method_${m.value}`} style={{margin: 0}}>{t(m.labelKey)}</label>
                        </span>
                    )}
                    {matchMethod === 'match_score' &&
                        <InputNumber name="min_match_score" value={minMatchScore}
                                     onValueChange={(e) => setMinMatchScore(e.value)}
                                     showButtons min={0} max={100} style={{width: '8em'}}/>}
                </div>
                {supplierModuleActive &&
                <div style={{marginTop: '.75em'}}>
                    <label style={{display: 'block', marginBottom: '.35em', color: '#6b7280', fontSize: '.85rem'}}>
                        {t('onboarding.match.matchTarget')}
                    </label>
                    <SelectButton value={matchTarget}
                                  options={MATCH_TARGET_OPTIONS.map(o => ({label: t(o.labelKey), value: o.value}))}
                                  onChange={(e) => { if (e.value) setMatchTarget(e.value); }}/>
                </div>}
            </Dialog>
        </div>
    );
};

export default MatchOnboardingStep;
